import { AppAlert, AppCard, AppLoadingSpinner, AppSearchBox } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useShiftServices } from '@opportunities/-hooks/useShiftServices'
import type { ShiftServiceListResponse } from 'backend/types'
import { useState } from 'react'
import { ServiceCard } from './ServiceCard'

/**
 * ServiceCardコンポーネントの形式に成形
 */
interface ShiftService {
  id: number
  serviceName: string
  serviceDescription: string
  serviceHint: string
  serviceUri: string
  serviceSegmentName: string
  serviceSegmentColor: string | null
  serviceCategoryName: string
  serviceCategoryColor: string | null
}

function mapService(raw: ShiftServiceListResponse['services'][number]): ShiftService {
  return {
    id: raw.id,
    serviceName: raw.service_name,
    serviceDescription: raw.service_description ?? '',
    serviceHint: raw.service_hint ?? '',
    serviceUri: raw.service_uri ?? '',
    serviceSegmentName: raw.service_segment_name ?? '',
    serviceSegmentColor: raw.service_segment_color,
    serviceCategoryName: raw.service_category_name ?? '',
    serviceCategoryColor: raw.service_category_color,
  }
}

export function ShiftServicesSection() {
  const [searchQuery, setSearchQuery] = useState('')

  // APIからサービス一覧を取得
  const { data, isLoading, error } = useShiftServices()

  const services: ShiftService[] = data?.services.map(mapService) ?? []

  // 検索フィルタリング
  const filteredServices = searchQuery
    ? services.filter(
        (service) =>
          service.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.serviceHint.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.serviceSegmentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.serviceCategoryName.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : services

  // コンテンツの描画
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='h-64 flex items-center justify-center'>
          <AppLoadingSpinner text='読み込み中...' size='default' />
        </div>
      )
    }

    if (error) {
      return (
        <AppAlert type='error'>
          サービス一覧の取得に失敗しました。
          {error.message && <div className='mt-1 text-xs opacity-80'>{error.message}</div>}
        </AppAlert>
      )
    }

    return (
      <div className='space-y-4'>
        <AppSearchBox
          label='サービスを検索'
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder='サービス名、セグメント名、カテゴリ名で絞り込み'
        />

        {filteredServices.length === 0 ? (
          <div className='text-center py-8 text-muted-foreground'>
            {services.length === 0 ? 'データがありません' : '検索結果がありませんでした'}
          </div>
        ) : (
          <div
            className='grid gap-4'
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            }}
          >
            {filteredServices.map((service) => (
              <ServiceCard key={service.id} service={service} onSearchClick={(query) => setSearchQuery(query)} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return <AppCard title='SHIFTのサービス一覧'>{renderContent()}</AppCard>
}
